// @givmo/board — the Form 990 page's address, read and written (CBG-YR5-VIEW; the backend's
// `board-form-990.md` §3). The Schedule O draft (CBG-YR5-SCHO) carries the same address, so the two
// pages read it with one set of rules.
//
// A year is named by its last day (`?tax_year_ends_on=`), never its named year: a change of year end
// can give two tax years one name. With no fiscal year end recorded, the month and day chosen ride in
// `?year_end_month=&year_end_day=`, stored nowhere else. `?line=` names a Part VI line to open on
// arrival (the Schedule O draft's "From your records" points at line 8a).

import type { Form990Address } from './form990-queries';

/** A calendar day as the address writes it, or `null`: `2025-02-30` is no day, so it is never sent. */
function dayParam(value: unknown): string | null {
  const parts = typeof value === 'string' ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(value) : null;
  if (parts === null) return null;
  const [year, month, day] = [Number(parts[1]), Number(parts[2]), Number(parts[3])];
  // Round-tripped, because `new Date(2025, 1, 30)` rolls over into March rather than failing.
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day
    ? parts[0]
    : null;
}

function wholeParam(value: unknown): number | null {
  if (typeof value === 'number' && Number.isInteger(value)) return value;
  if (typeof value === 'string' && /^\d{1,2}$/.test(value)) return Number(value);
  return null;
}

export function addressOf(search: Record<string, unknown>): Form990Address {
  return {
    taxYearEndsOn: dayParam(search.tax_year_ends_on),
    yearEndMonth: wholeParam(search.year_end_month),
    yearEndDay: wholeParam(search.year_end_day),
  };
}

export function searchOf(address: Form990Address): Record<string, string | number> {
  return {
    ...(address.taxYearEndsOn === null ? {} : { tax_year_ends_on: address.taxYearEndsOn }),
    ...(address.yearEndMonth === null ? {} : { year_end_month: address.yearEndMonth }),
    ...(address.yearEndDay === null ? {} : { year_end_day: address.yearEndDay }),
  };
}

/** A line the address asks to open (`?line=8a`), or `null`: a line id is a number and a letter. */
export function lineParam(value: unknown): string | null {
  return typeof value === 'string' && /^\d{1,2}[a-z]?$/.test(value) ? value : null;
}
